import { useState } from 'react'
import { router, useForm, usePage } from '@inertiajs/react'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Button } from '@/shared/ui/button'
import { ALL_DAYS_ON } from '@/shared/ui/weekly-schedule-grid'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'
import { t } from '@/shared/lib/i18n'
import { instrumentsPath } from '@/shared/lib/routes'
import { useFollowParentUnitSchedulePreview } from '@/entities/instruments/useFollowParentUnitSchedulePreview'
import {
  instrumentTypesFilterQuery,
  type InstrumentTypesReturnFilters,
} from '@/shared/lib/instrument-edit-return-to'
import { buildInstrumentFormPayload } from './buildInstrumentFormPayload'
import { parseOptionalId } from './parseOptionalId'
import {
  InstrumentFormFields,
  type InstrumentFormData,
  type InstrumentFormErrors,
  type InstrumentFormSelectOption,
} from './InstrumentFormFields'

export interface InstrumentFormOptions {
  instrumentTypeOptions: InstrumentFormSelectOption[]
  siteOptions: InstrumentFormSelectOption[]
  departmentOptions: InstrumentFormSelectOption[]
  unitOptions: InstrumentFormSelectOption[]
  parentDaySchedule: WeekdaySchedule
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  instrumentTypeId: number
  instrumentTypeName: string
  options: InstrumentFormOptions
  /** Origin to redirect back to after a successful create (sent as return_to param). */
  returnTo?: string
  /** Filters/expanded type active on the Instrument Types index, so a successful create
   * lands back on the same view instead of a reset one (REBUILD-388/JW 386). */
  returnFilters?: InstrumentTypesReturnFilters
}

interface ParentDaySchedulePageProps {
  parentDaySchedule?: WeekdaySchedule
  [key: string]: unknown
}

const buildInitialData = (instrumentTypeId: number): InstrumentFormData => ({
  instrumentLabel: '',
  serialNumber: '',
  barcodeId: '',
  cost: '',
  dateOfPurchase: '',
  instrumentTypeId: instrumentTypeId.toString(),
  followParentSchedule: true,
  customDaySchedule: { ...ALL_DAYS_ON },
  siteId: '',
  departmentId: '',
  unitId: '',
  tagIds: [],
})

interface InstrumentFormDialogBodyProps {
  instrumentTypeId: number
  instrumentTypeName: string
  options: InstrumentFormOptions
  onOpenChange: (open: boolean) => void
  returnTo?: string
  returnFilters?: InstrumentTypesReturnFilters
}

/**
 * "Add Instrument" modal body (REBUILD-103). Mounts only while the dialog is open so
 * form + parent-preview hook state reset synchronously — no stale unit schedule flash
 * from a prior session. Options come in as Inertia props (no separate JSON endpoint),
 * the type is locked to the page's type, and it submits through the standard
 * `POST /instruments` Inertia flow — a success redirects to the new instrument (or
 * back to the Instrument Types index when `returnTo` is set), validation errors
 * populate inline.
 */
const InstrumentFormDialogBody = ({
  instrumentTypeId,
  instrumentTypeName,
  options,
  onOpenChange,
  returnTo,
  returnFilters,
}: InstrumentFormDialogBodyProps) => {
  const form = useForm<InstrumentFormData>(buildInitialData(instrumentTypeId))
  // Prefer top-level page prop: partial reload only: ['parentDaySchedule'] updates that
  // key, while nested instrumentFormOptions.parentDaySchedule can lag until a full visit.
  const pageParentDaySchedule = usePage<ParentDaySchedulePageProps>().props.parentDaySchedule
  const parentDaySchedule = pageParentDaySchedule ?? options.parentDaySchedule

  // Exhausted preview: bump to retry while Follow stays ON (single-toggle cannot use
  // Departments' multi-row Follow trick).
  const [previewRetryNonce, setPreviewRetryNonce] = useState(0)
  const selectedUnitId = parseOptionalId(form.data.unitId)
  const { effectiveParentPreview, parentPreviewPending, parentPreviewFailed } =
    useFollowParentUnitSchedulePreview({
      enabled: form.data.followParentSchedule,
      unitId: selectedUnitId,
      parentDaySchedule,
      initialUnitId: null,
      retryNonce: previewRetryNonce,
    })

  const errors = form.errors as InstrumentFormErrors

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    const nextErrors: Record<string, string> = {}
    if (!form.data.siteId) nextErrors.siteId = t('instruments.form.siteRequired')
    if (!form.data.departmentId) nextErrors.departmentId = t('instruments.form.departmentRequired')
    if (!form.data.unitId) nextErrors.unitId = t('instruments.form.unitRequired')
    if (Object.keys(nextErrors).length > 0) {
      form.setError(nextErrors)
      return
    }

    // This dialog only ever creates (the type is locked to the page's type).
    form.transform((data) => ({
      ...buildInstrumentFormPayload(data, false),
      ...(returnTo ? { return_to: returnTo, ...instrumentTypesFilterQuery(returnFilters) } : {}),
    }))
    form.post(instrumentsPath(), { onSuccess: () => onOpenChange(false) })
  }

  return (
    <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>
          {t('instruments.form.addInstrumentDialogTitle', { type: instrumentTypeName })}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.base && (
          <div className="rounded border border-destructive bg-destructive/10 px-4 py-3">
            <p className="text-sm text-destructive">{errors.base}</p>
          </div>
        )}

        <InstrumentFormFields
          form={form}
          errors={errors}
          instrumentTypeOptions={options.instrumentTypeOptions}
          siteOptions={options.siteOptions}
          departmentOptions={options.departmentOptions}
          unitOptions={options.unitOptions}
          parentDaySchedule={effectiveParentPreview}
          parentPreviewPending={parentPreviewPending}
          parentPreviewFailed={parentPreviewFailed}
          onParentPreviewRetry={() => setPreviewRetryNonce((nonce) => nonce + 1)}
          isTypeLocked
          idPrefix="dialog-"
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('instruments.form.cancelButton')}
          </Button>
          <Button type="submit" disabled={form.processing}>
            {t('instruments.form.submitCreate')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}

/**
 * Drop unit_id left on Index/Planning by parent-preview partial reloads when the modal
 * closes. Use Inertia (not history.replaceState) so page.url stays in sync — a bare
 * replaceState leaves Inertia still sending ?unit_id= on later visits.
 */
const clearUnitIdFromParentPreviewUrl = () => {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  if (!url.searchParams.has('unit_id')) return

  url.searchParams.delete('unit_id')
  router.get(
    `${url.pathname}${url.search}${url.hash}`,
    {},
    {
      replace: true,
      preserveState: true,
      preserveScroll: true,
      only: ['parentDaySchedule'],
    },
  )
}

export const InstrumentFormDialog = ({
  open,
  onOpenChange,
  instrumentTypeId,
  instrumentTypeName,
  options,
  returnTo,
}: Props) => {
  // Mount body only while open so form + parent-preview hook reset synchronously
  // on each open (no stale unit schedule flash from a prior session).
  // Must wrap Cancel + overlay/ESC + success close — body used to call onOpenChange
  // directly and skip URL cleanup on Cancel.
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) clearUnitIdFromParentPreviewUrl()
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {open ? (
        <InstrumentFormDialogBody
          key={instrumentTypeId}
          instrumentTypeId={instrumentTypeId}
          instrumentTypeName={instrumentTypeName}
          options={options}
          onOpenChange={handleOpenChange}
          returnTo={returnTo}
        />
      ) : null}
    </Dialog>
  )
}

export default InstrumentFormDialog
